import type React from "react";
import classNames from "classnames";
import { ArrowRightIcon, PlusIcon } from "@icons";
import type { ResolvedAnatomyBinding } from "./componentAnatomy";
import {
    bindingReader,
    Part,
    type AnatomyPartHighlight,
} from "./componentAnatomyParts";
import styles from "./ComponentAnatomyView.module.scss";

/**
 * X-ray plate for Link: focus ring, inset focus line, focus fill, label and
 * icons. The variant id is the content axis, so it picks which icons to draw.
 */
export const LinkSchematic = ({
    bindings,
    highlight,
    variantId,
}: {
    bindings: readonly ResolvedAnatomyBinding[];
    highlight: AnatomyPartHighlight;
    variantId: string;
}) => {
    const read = bindingReader(bindings);
    const radius = read("border", "border-radius");
    const ringSpread = read("focusRing", "box-shadow (spread)");
    const ringColor = read("focusRing", "box-shadow (color)");
    const lineWidth = read("border", "box-shadow (inset width)");
    const lineColor = read("border", "box-shadow (inset color)");
    const labelColor = read("label", "color");
    const isIconOnly = variantId === "iconOnly";
    const hasLeftIcon = variantId === "leftIcon" || variantId === "bothIcons";
    const hasRightIcon = variantId === "rightIcon" || variantId === "bothIcons";

    const renderGlyph = (icon: React.ReactNode) => (
        <Part
            partId="glyph"
            highlight={highlight}
            className={styles.linkGlyph}
            style={{
                width: read("glyph", "width"),
                height: read("glyph", "height"),
            }}
        >
            {icon}
        </Part>
    );

    return (
        <span className={classNames(styles.schematic, styles.buttonSchematic)}>
            {ringSpread !== undefined && ringColor !== undefined && (
                <Part
                    partId="focusRing"
                    highlight={highlight}
                    className={styles.buttonRing}
                    hitInset={`calc(-1 * ${ringSpread})`}
                    style={{
                        borderRadius: radius,
                        boxShadow: `0 0 0 ${ringSpread} ${ringColor}`,
                    }}
                />
            )}
            {lineWidth !== undefined && lineColor !== undefined && (
                <Part
                    partId="border"
                    highlight={highlight}
                    className={styles.buttonBorder}
                    style={{
                        borderColor: lineColor,
                        borderWidth: lineWidth,
                        borderRadius: radius,
                    }}
                />
            )}
            <Part
                partId="background"
                highlight={highlight}
                className={styles.buttonBackground}
                style={{
                    backgroundColor: read("background", "background-color"),
                    borderRadius: radius,
                }}
            />
            <span
                className={classNames(styles.buttonContent, styles.linkContent)}
                style={{ gap: read("background", "gap"), color: labelColor }}
            >
                {hasLeftIcon && renderGlyph(<PlusIcon />)}
                {isIconOnly ? (
                    renderGlyph(<ArrowRightIcon />)
                ) : (
                    <Part
                        partId="label"
                        highlight={highlight}
                        className={styles.buttonLabel}
                        style={{
                            color: labelColor,
                            fontSize: read("label", "font-size"),
                            fontWeight: read("label", "font-weight"),
                            lineHeight: read("label", "line-height"),
                            letterSpacing: read("label", "letter-spacing"),
                        }}
                    >
                        Reports
                    </Part>
                )}
                {hasRightIcon && renderGlyph(<ArrowRightIcon />)}
            </span>
        </span>
    );
};
